import { ArrowLeft, Globe, Palette } from 'lucide-react';

// ─── DATA DE TOUS LES PROJETS ───────────────────────────────────────────────

const allProjects = {
  Portfolio_Alexandre_Kotowicz_React: {
    slug: "Portfolio_Alexandre_Kotowicz_React",
    category: "web",
    color: "bg-cyan-400",
    accentColor: "bg-cyan-400",
    titleShort: "Portfolio A. Kotowicz",
    intro: "Développement d'un portfolio interactif pour un développeur de jeux vidéo en recherche d'emploi au Canada.",
    sections: [
      {
        title: "Contexte du projet",
        text: "Alexandre Kotowicz est un étudiant français résidant à Montréal, spécialisé dans le développement de jeux vidéo. En phase de recherche d'emploi au Canada, il avait besoin d'un portfolio professionnel pour mettre en valeur ses compétences et ses réalisations auprès des entreprises du secteur."
      },
      {
        title: "Analyse des besoins",
        text: "Le besoin était clair : créer un site web capable de rassembler et présenter l'ensemble de ses projets (personnels, académiques, professionnels et techniques) dans un seul et même espace. Le portfolio devait être facilement navigable, visuellement attractif et refléter son expertise en développement de jeux vidéo."
      },
      {
        title: "Architecture du site",
        text: "J'ai structuré le site pour offrir une navigation intuitive entre les différentes catégories de projets. Chaque projet bénéficie d'une page dédiée avec description détaillée, captures d'écran, vidéos de gameplay et liens vers les repositories ou versions jouables."
      },
      {
        title: "Développement avec React",
        text: "Le portfolio a été développé avec React pour garantir une expérience utilisateur moderne et réactive. J'ai implémenté des animations subtiles, des transitions fluides et un système de filtrage des projets par catégorie. Le site est entièrement responsive et optimisé pour tous les types d'appareils."
      },
      {
        title: "Design et identité visuelle",
        text: "Le design du site reflète l'univers du jeu vidéo tout en restant professionnel et épuré. La palette de couleurs et les éléments graphiques ont été choisis pour mettre en avant les projets sans détourner l'attention. Chaque section est pensée pour guider naturellement le regard vers les informations essentielles."
      },
      {
        title: "Impact professionnel",
        text: "Ce portfolio a permis à Alexandre de présenter ses compétences de manière professionnelle lors de candidatures et d'entretiens d'embauche. Le site centralise toutes ses réalisations et facilite la découverte de son travail par les recruteurs. C'est devenu un outil essentiel dans sa démarche de recherche d'emploi au Canada."
      }
    ],
    tags: ["React", "JavaScript", "Responsive", "Portfolio", "Etudiant"],
  },

  Clip_Web_Site: {
    slug: "Clip_Web_Site",
    category: "web",
    color: "bg-violet-600",
    accentColor: "bg-violet-600",
    titleShort: "Site web Clip Web",
    intro: "Développement complet de mon site vitrine professionnel avec React : de la maquette au déploiement, en passant par le SEO et la promotion digitale.",
    sections: [
      {
        title: "Genèse du projet",
        text: "Le site web Clip Web représente la vitrine digitale de mon activité de freelance. L'objectif était de créer un espace professionnel permettant de présenter mes services, mon portfolio et de faciliter la prise de contact avec des clients potentiels."
      },
      {
        title: "Phase de conception et design",
        text: "Avant toute ligne de code, j'ai consacré un temps important à la phase de conception. J'ai créé des maquettes détaillées pour chaque page du site, en réfléchissant à l'expérience utilisateur et au parcours de navigation. Parallèlement, j'ai développé une charte graphique complète définissant les couleurs, typographies et styles visuels."
      },
      {
        title: "Développement avec React",
        text: "Le site a été développé avec React pour bénéficier d'une architecture moderne et performante. J'ai mis en place une structure de composants réutilisables, des animations fluides et une navigation intuitive. Le code est organisé et optimisé pour faciliter les futures évolutions."
      },
      {
        title: "Stratégie SEO",
        text: "Une attention particulière a été portée au référencement naturel pour maximiser la visibilité du site. J'ai optimisé la structure HTML sémantique, les balises meta, les temps de chargement et le contenu textuel. Cette stratégie permet à Clip Web d'apparaître dans les résultats de recherche pour des requêtes pertinentes."
      },
      {
        title: "Responsive design",
        text: "Le site est entièrement responsive et adapté à tous les types d'écrans : desktop, tablette et mobile. Chaque composant a été pensé pour offrir une expérience optimale quel que soit le dispositif utilisé."
      },
      {
        title: "Résultat et évolution",
        text: "Le site Clip Web est aujourd'hui opérationnel et constitue mon principal outil de prospection. Il regroupe mon portfolio, mes services et mes coordonnées. Le site évolue régulièrement avec l'ajout de nouveaux projets et l'amélioration continue des fonctionnalités."
      }
    ],
    tags: ["React", "SEO", "Responsive", "Tailwind CSS"],
  },

  Clip_Web_Site_V2: {
    slug: "Clip_Web_Site_V2",
    category: "web",
    color: "bg-yellow-400",
    accentColor: "bg-yellow-400",
    titleShort: "Nouveau Site Clip Web",
    intro: "Version améliorée et rénovée de mon site vitrine professionnel avec React : refonte complète du design, nouvelles fonctionnalités et optimisations de performance.",
    sections: [
      {
        title: "Genèse de la refonte",
        text: "Après le succès de la première version, j'ai décidé de créer une version 2 du site Clip Web pour refléter l'évolution de mon expertise et améliorer l'expérience utilisateur. Cette refonte était basée sur les retours clients et mes observations du comportement utilisateur."
      },
      {
        title: "Modernisation du design",
        text: "La nouvelle version présente un design plus épuré et moderne, avec une meilleure hiérarchie visuelle. J'ai repensé les couleurs, la typographie et la mise en page pour offrir une expérience plus cohérente et attrayante. Les animations ont été affinées pour créer une sensation de fluidité et de professionnalisme."
      },
      {
        title: "Optimisations techniques",
        text: "Cette nouvelle version bénéficie d'optimisations importantes : meilleure performance grâce à la lazy loading des images, code plus efficace et mieux structuré, amélioration du SEO avec des meta-tags dynamiques, et optimisations pour les Core Web Vitals."
      },
      {
        title: "Architecture améliorée",
        text: "J'ai complètement refondu l'architecture du projet pour la rendre plus scalable et maintenable. Les composants sont mieux organisés, le state management est simplifié, et le code est plus documenté. Cette architecture facilite les évolutions futures et les ajouts de nouvelles fonctionnalités."
      },
      {
        title: "Impact et résultats",
        text: "La version 2 de Clip Web représente une évolution significative tant sur le plan visuel que fonctionnel. Elle continue à être mon principal outil de prospection et reflète ma progression en tant que développeur. Le site attire désormais plus de visiteurs et génère de meilleurs taux de conversion."
      }
    ],
    tags: ["React", "SEO", "Performance", "Tailwind CSS", "Refonte"],
  },

  Mulligan_TCG_Site_Web: {
    slug: "Mulligan_TCG_Site_Web",
    category: "web",
    color: "bg-orange-600",
    accentColor: "bg-orange-600",
    titleShort: "Site web Mulligan TCG",
    intro: "Développement web collaboratif pour une communauté de jeux de cartes à collectionner en Normandie. Projet académique Ynov Campus.",
    sections: [
      {
        title: "Présentation du projet",
        text: "Mulligan est un projet académique développé dans le cadre des Ydays à Ynov Campus Rouen. L'objectif est de créer une plateforme dédiée au Trading Card Game (TCG) en Normandie, visant à fédérer une communauté de joueurs passionnés autour d'événements, d'échanges et de tournois."
      },
      {
        title: "Travail collaboratif pluridisciplinaire",
        text: "Ce projet se distingue par sa dimension collaborative enrichissante. Je travaille aux côtés d'étudiants M1 en graphisme d'Ynov, chacun apportant son expertise spécifique : design graphique, développement web, audiovisuel et communication."
      },
      {
        title: "Mon rôle : le développement web",
        text: "Ma responsabilité se concentre sur le développement technique du site web. Je prends en charge l'analyse des besoins fonctionnels, l'architecture de l'application et l'implémentation des fonctionnalités, en collaboration étroite avec l'équipe design."
      },
      {
        title: "Fonctionnalités du site",
        text: "Le site Mulligan proposera : présentation de la communauté, calendrier des événements et tournois, système d'inscription, espace d'échange entre joueurs et actualités du monde TCG en Normandie. L'objectif est de créer un véritable hub pour les passionnés."
      },
      {
        title: "Projet en cours de développement",
        text: "Mulligan est actuellement en phase de développement actif. Les fonctionnalités sont progressivement implémentées et testées. Le site n'est pas encore disponible en ligne publiquement, mais l'évolution du projet est constante."
      },
      {
        title: "Compétences développées",
        text: "Ce projet me permet de développer des compétences techniques (développement web, gestion de projet) et transversales (travail en équipe pluridisciplinaire, communication inter-métiers, respect des deadlines). C'est une expérience concrète simulant les conditions réelles d'une collaboration en agence."
      }
    ],
    tags: ["Symfony", "Projet académique", "Ynov Campus", "Travail d'équipe", "TCG"],
  },

  Clip_Web_Flyer_Digital: {
    slug: "Clip_Web_Flyer_Digital",
    category: "design",
    color: "bg-yellow-400",
    accentColor: "bg-yellow-400",
    titleShort: "Flyer numérique Clip Web",
    intro: "Création complète de mon identité de freelance : site web, charte graphique, logo et supports de communication digitaux.",
    sections: [
      {
        title: "Phase de conception",
        text: "Le projet a débuté par une phase de réflexion approfondie sur mon positionnement et mon identité visuelle. J'ai créé des maquettes pour le site web, développé une charte graphique cohérente, et conçu un logo qui reflète mon approche du design : moderne, épuré et professionnel."
      },
      {
        title: "Référencement et visibilité",
        text: "Une attention particulière a été portée au référencement naturel (SEO) pour assurer la visibilité du site sur les moteurs de recherche. J'ai optimisé la structure du code, les balises meta, les temps de chargement et le contenu textuel."
      },
      {
        title: "Promotion sur les réseaux sociaux",
        text: "Pour développer ma visibilité, j'ai mis en place une stratégie de communication sur LinkedIn et Facebook. Le site a été partagé via mon réseau, accompagné de contenus mettant en avant mes réalisations et mon expertise."
      },
      {
        title: "Création du flyer numérique",
        text: "Pour compléter ma présence en ligne, j'ai conçu un flyer numérique synthétique et impactant. Ce support digital permet de présenter rapidement mes services avec un visuel attractif. Le format numérique facilite le partage par email, sur les réseaux sociaux ou lors d'échanges professionnels."
      },
      {
        title: "Résultat",
        text: "Clip Web dispose aujourd'hui d'une identité visuelle forte et cohérente, d'un site web professionnel et performant, et d'outils de communication efficaces. Cette présence digitale complète me permet de présenter mes services et d'attirer des clients potentiels."
      }
    ],
    tags: ["Design graphique", "Communication", "Identité visuelle"],
  },

  JJ_Detailing_Carte_Visite: {
    slug: "JJ_Detailing_Carte_Visite",
    category: "design",
    color: "bg-cyan-400",
    accentColor: "bg-cyan-400",
    titleShort: "Carte de visite JJ Detailing",
    intro: "Création d'une identité visuelle professionnelle pour une entreprise de detailing automobile en pleine expansion.",
    sections: [
      {
        title: "Contexte du projet",
        text: "JJ Detailing, une entreprise de detailing automobile souhaitant développer sa notoriété, m'a contacté pour travailler sur son identité visuelle. Le projet a débuté par la refonte d'un logo existant qui nécessitait une modernisation pour mieux refléter le professionnalisme de l'entreprise."
      },
      {
        title: "Refonte du logo",
        text: "La première étape a consisté à retravailler le logo existant. L'objectif était de conserver l'essence de la marque tout en apportant une touche plus moderne et professionnelle. Cette nouvelle identité visuelle allait servir de base pour l'ensemble des supports de communication."
      },
      {
        title: "Conception de la carte de visite",
        text: "Une fois le logo finalisé, j'ai pu me concentrer sur la création de la carte de visite. Le client m'a fourni les informations essentielles à intégrer : coordonnées, services proposés et slogan. Mon rôle a été de structurer ces éléments de manière harmonieuse en créant un design épuré et professionnel."
      },
      {
        title: "Approche design",
        text: "Le design adopte une palette de couleurs sobre et premium, avec une typographie claire et élégante. La mise en page équilibrée met en valeur le logo tout en assurant une lisibilité optimale des coordonnées. Des éléments graphiques subtils évoquant l'univers automobile enrichissent le visuel."
      },
      {
        title: "Résultat",
        text: "La carte de visite imprimée sur papier premium avec finition mate renforce l'image haut de gamme de JJ Detailing. Ce support de communication efficace permet au client de se présenter professionnellement auprès de sa clientèle et de se démarquer dans le secteur du detailing automobile."
      }
    ],
    tags: ["Identité visuelle", "Carte de visite", "Automobile"],
  },

  JJ_Detailing_Flyer: {
    slug: "JJ_Detailing_Flyer",
    category: "design",
    color: "bg-violet-600",
    accentColor: "bg-violet-600",
    titleShort: "Flyer JJ Detailing",
    intro: "Conception d'un support de communication print pour promouvoir l'activité professionnelle d'une entreprise de detailing automobile.",
    sections: [
      {
        title: "Suite de la collaboration",
        text: "Après le succès de la carte de visite, JJ Detailing est revenu vers moi pour un nouveau projet : la création d'un flyer promotionnel. L'objectif était de disposer d'un support imprimé permettant de présenter l'ensemble des services proposés et d'attirer de nouveaux clients."
      },
      {
        title: "Processus collaboratif",
        text: "Ce projet s'est déroulé dans un esprit de collaboration étroite. Le client m'a transmis ses besoins spécifiques ainsi que les textes à intégrer pour chaque service. De mon côté, je me suis concentré sur le design et la mise en forme du flyer, en veillant à créer une composition visuelle attractive et efficace."
      },
      {
        title: "Structure du flyer",
        text: "Le format recto/verso a été choisi pour optimiser l'espace disponible. Le recto présente une accroche visuelle impactante avec les services phares et les coordonnées. Le verso détaille l'ensemble des prestations : lavage extérieur, nettoyage intérieur, polish, traitement céramique, etc."
      },
      {
        title: "Cohérence visuelle",
        text: "Le design du flyer s'inscrit dans la continuité de l'identité visuelle établie avec la carte de visite. La palette de couleurs, la typographie et les éléments graphiques assurent une cohérence parfaite entre tous les supports de communication."
      },
      {
        title: "Utilisation et diffusion",
        text: "Le flyer, imprimé au format A5 pour une prise en main optimale, est distribué aux clients potentiels, déposé chez des commerces partenaires et utilisé lors d'événements automobiles locaux."
      }
    ],
    tags: ["Recto/verso", "Communication", "Automobile"],
  },

  AL_Batiment_Carte_Visite: {
    slug: "AL_Batiment_Carte_Visite",
    category: "design",
    color: "bg-orange-600",
    accentColor: "bg-orange-600",
    titleShort: "Carte de visite AL Bâtiment",
    intro: "Mon premier projet professionnel : création complète d'une identité visuelle pour une entreprise du bâtiment, du logo à la carte de visite.",
    sections: [
      {
        title: "Premier projet professionnel",
        text: "AL Bâtiment représente mon tout premier projet professionnel. Le client a entendu parler de mes services via le bouche-à-oreille et m'a contacté pour concevoir sa carte de visite. Ce projet a été particulièrement formateur car il m'a permis de découvrir la réalité du travail avec un client."
      },
      {
        title: "Le défi : partir de zéro",
        text: "L'entreprise ne disposait d'aucune identité visuelle préexistante, pas même un logo. Ce projet nécessitait donc une approche complète : comprendre l'activité du client, identifier ses valeurs et ses besoins, puis créer de A à Z une identité visuelle cohérente et professionnelle."
      },
      {
        title: "Création du logo",
        text: "La première étape a été de concevoir un logo représentatif de l'entreprise. J'ai pris le temps d'échanger avec le client pour comprendre ses attentes : fiabilité, savoir-faire artisanal et professionnalisme. Le logo devait être simple, mémorable et adapté au secteur du bâtiment."
      },
      {
        title: "Design de la carte de visite",
        text: "Une fois le logo validé, j'ai pu concevoir la carte de visite. Le design adopte des codes visuels propres au secteur du bâtiment : palette de couleurs évoquant solidité et confiance, typographie robuste et claire. La mise en page équilibrée met en valeur le logo et assure une excellente lisibilité."
      },
      {
        title: "Une expérience formatrice",
        text: "Ce projet m'a permis d'acquérir une expérience précieuse : gestion de la relation client, prise de brief, itérations sur les propositions, respect des délais et livraison finale. Cette première expérience professionnelle a posé les bases de ma méthodologie de travail."
      }
    ],
    tags: ["Logo", "Identité visuelle", "Bâtiment"],
  },
};

// ─── COMPOSANT PAGE PROJET ──────────────────────────────────────────────────

export function ProjectPage({ slug, onBack }) {
  const project = allProjects[slug];

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl sm:text-4xl font-black mb-4">Projet introuvable</h1>
          <button
            onClick={onBack}
            className="bg-black text-white px-6 py-3 font-black border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-none hover:translate-x-1.5 hover:translate-y-1.5 transition-all"
          >
            ← RETOUR
          </button>
        </div>
      </div>
    );
  }

  const isWeb = project.category === 'web';

  return (
    <div className="min-h-screen bg-[#F0F8FF]">
      {/* Header de la page projet */}
      <div className={`${project.color} border-b-4 border-black py-8 px-4 sm:py-12 sm:px-6 relative overflow-hidden`}>
        {/* Décorations */}
        <div className="hidden sm:block absolute top-6 right-10 w-10 h-10 bg-black opacity-20 rotate-45" />
        <div className="hidden sm:block absolute bottom-4 left-1/3 w-6 h-6 bg-white opacity-30 rounded-full" />

        <div className="max-w-5xl mx-auto">
          {/* Bouton retour */}
          <button
            onClick={onBack}
            className="flex items-center gap-2 font-black text-sm sm:text-base mb-5 sm:mb-8 bg-black text-white px-3 py-2 sm:px-4 border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,0.4)] sm:shadow-[6px_6px_0px_0px_rgba(0,0,0,0.4)] hover:shadow-none hover:translate-x-1.5 hover:translate-y-1.5 transition-all"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            RETOUR AUX PROJETS
          </button>

          {/* Badge catégorie */}
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="bg-white border-4 border-black p-1.5 sm:p-2 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] sm:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              {isWeb ? <Globe className="w-5 h-5 sm:w-6 sm:h-6" /> : <Palette className="w-5 h-5 sm:w-6 sm:h-6" />}
            </div>
            <span className="bg-black text-white px-3 sm:px-4 py-1 font-black text-xs sm:text-sm border-2 border-black">
              {isWeb ? 'SITE WEB' : 'DESIGN GRAPHIQUE'}
            </span>
          </div>

          {/* Titre */}
          <h1 className="text-2xl min-[400px]:text-3xl sm:text-4xl md:text-6xl font-black leading-tight mb-3 sm:mb-4 break-words">
            {project.titleShort.toUpperCase()}
          </h1>

          {/* Intro */}
          <p className="text-base sm:text-lg md:text-xl font-bold max-w-3xl leading-relaxed">
            {project.intro}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-4 sm:mt-6">
            {project.tags.map((tag, i) => (
              <span
                key={i}
                className="bg-white border-2 border-black px-2 sm:px-3 py-0.5 sm:py-1 font-black text-[11px] sm:text-xs shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] sm:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu des sections */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-7 sm:space-y-8">
        {project.sections.map((section, index) => (
          <div
            key={index}
            className={`bg-white border-4 border-black p-4 pt-6 sm:p-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] relative`}
          >
            {/* Numéro de section */}
            <div className={`absolute -top-4 -left-3 sm:-top-5 sm:-left-5 ${project.color} border-4 border-black w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center font-black text-base sm:text-xl shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] sm:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`}>
              {String(index + 1).padStart(2, '0')}
            </div>

            <h2 className="text-lg sm:text-2xl md:text-3xl font-black mb-3 sm:mb-4 mt-2 leading-tight">{section.title}</h2>
            <p className="text-[15px] sm:text-base md:text-lg leading-relaxed font-medium text-gray-800">
              {section.text}
            </p>
          </div>
        ))}

        {/* CTA bas de page */}
        <div className="bg-black text-white p-5 sm:p-8 border-4 border-black shadow-[5px_5px_0px_0px_rgba(255,200,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(255,200,0,1)] text-center mt-8 sm:mt-12">
          <h3 className="text-xl sm:text-3xl font-black mb-2">UN PROJET SIMILAIRE ?</h3>
          <p className="font-bold text-sm sm:text-base mb-5 sm:mb-6 text-gray-300">Parlons-en, je serais ravi de vous accompagner.</p>
          <button
            onClick={() => {
              onBack();
              setTimeout(() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="bg-yellow-400 text-black w-full sm:w-auto px-5 py-3 sm:px-8 sm:py-4 font-black text-base sm:text-lg border-4 border-yellow-400 shadow-[4px_4px_0px_0px_rgba(255,200,0,0.4)] sm:shadow-[8px_8px_0px_0px_rgba(255,200,0,0.4)] hover:shadow-[2px_2px_0px_0px_rgba(255,200,0,0.4)] sm:hover:shadow-[4px_4px_0px_0px_rgba(255,200,0,0.4)] hover:translate-x-1 hover:translate-y-1 transition-all"
          >
            CONTACTEZ-MOI →
          </button>
        </div>
      </div>
    </div>
  );
}
